/**
 * Maps a catalogue Location onto the payload `createSite` expects, so an
 * existing Location can be pulled into an evaluation as a site.
 *
 * Mirrors the server-side mapping in
 * `backend/utilities/evaluationPortfolioSync.js`, minus `sourcePortfolio` —
 * a site added by hand from the picker did not come from a portfolio sync,
 * and stamping that field would make a later sync think it owns the site
 * and unlink it.
 *
 * Pure, so the renames can be tested directly.
 */

export interface LocationLike {
	_id: string
	assetName?: string
	address?: string
	city?: string
	stateProvince?: string
	postalCode?: string
	latitude?: number
	longitude?: number
	googlePlaceId?: string
	image?: string
}

export interface SiteFromLocationPayload {
	name: string
	address?: string
	city?: string
	state?: string
	postalCode?: string
	latitude?: number
	longitude?: number
	googlePlaceId?: string
	location: string
	imageUrls?: string[]
}

export function siteFromLocation(location: LocationLike): SiteFromLocationPayload {
	return {
		// Location.assetName has a `required` default of 'na', but a blank
		// value would still fail the site's own required-name validation.
		name: location.assetName || 'Unnamed location',
		address: location.address,
		city: location.city,
		state: location.stateProvince,
		postalCode: location.postalCode,
		// Passing coordinates through makes createSite stamp
		// geocodeStatus: 'PROVIDED' instead of re-geocoding an address the
		// catalogue already resolved. Leaving them undefined lets it fall
		// back to address geocoding, which is the right behaviour for a
		// Location that was never geocoded.
		latitude: location.latitude,
		longitude: location.longitude,
		googlePlaceId: location.googlePlaceId,
		// The link back to the catalogue. Also what stops the same Location
		// being offered twice in the picker.
		location: location._id,
		imageUrls: location.image ? [location.image] : undefined,
	}
}
